import { useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import { X, Trash2, Plus, ChevronDown, ChevronRight } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useEffectiveOutcomes } from '@/hooks/useOutcomes';

import type {
  SmsActionConfig,
  CreateSmsActionConfigDto,
  ResultHandlerConfig,
} from '@repo/shared-types';

const HANDLER_TYPES = [
  { value: 'UPDATE_CALL_OUTCOME', label: 'Update Call Outcome' },
  { value: 'SEND_EMAIL', label: 'Send Email Notification' },
];

const OUTCOME_OPTIONS = ['CONFIRMED', 'NOT_CONFIRMED', 'TIMED_OUT'];

// Static fallback while the dynamic outcome definitions load (matchable
// outcomes = the retryable/unanswered infra set).
const CALL_OUTCOMES_FALLBACK = ['NO_ANSWER', 'BUSY', 'VOICEMAIL', 'CANCELED', 'FAILED'];

const DEFAULT_OUTCOME_MAP: Record<string, string> = {
  CONFIRMED: 'SHIFT_CONFIRMED',
  NOT_CONFIRMED: 'SHIFT_DECLINED',
  TIMED_OUT: 'SHIFT_DECLINED',
};

interface SmsActionConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: SmsActionConfig | null;
  onSubmit: (values: CreateSmsActionConfigDto) => void;
  isLoading?: boolean;
}

export const SmsActionConfigModal = ({
  open,
  onOpenChange,
  config,
  onSubmit,
  isLoading = false,
}: SmsActionConfigModalProps) => {
  // Key resets all form state when config changes or modal reopens
  const formKey = config ? `edit-${config.id}` : `new-${open}`;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60" />
        <Dialog.Content className="bg-background fixed top-1/2 left-1/2 z-50 w-full max-w-4xl -translate-x-1/2 -translate-y-1/2 rounded-lg p-6 shadow-lg">
          <SmsActionConfigForm
            key={formKey}
            config={config}
            onSubmit={onSubmit}
            isLoading={isLoading}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const SmsActionConfigForm = ({
  config,
  onSubmit,
  isLoading,
}: {
  config: SmsActionConfig | null;
  onSubmit: (values: CreateSmsActionConfigDto) => void;
  isLoading: boolean;
}) => {
  const isEditing = config !== null;
  const { data: campaignsData } = useCampaigns();
  const campaigns = campaignsData?.data || campaignsData || [];
  // Matchable call outcomes — retryable set from the dynamic vocabulary.
  const { data: outcomeDefinitions } = useEffectiveOutcomes();
  const callOutcomes =
    outcomeDefinitions && outcomeDefinitions.length > 0
      ? outcomeDefinitions.filter((d) => d.is_retryable).map((d) => d.key)
      : CALL_OUTCOMES_FALLBACK;

  const [name, setName] = useState(config?.name || '');
  const [isActive, setIsActive] = useState(config?.is_active ?? true);
  const [messageType, setMessageType] = useState<'FOLLOWUP_SMS' | 'MISSED_CALL_SMS'>(
    config?.message_type || 'FOLLOWUP_SMS',
  );
  const [campaignMode, setCampaignMode] = useState<'INCLUDE' | 'EXCLUDE'>(
    config?.campaign_mode || 'EXCLUDE',
  );
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>(
    config?.campaigns?.map((c) => c.campaign_id) || [],
  );
  const [actionType, setActionType] = useState<'LLM_CONFIRMATION'>(
    config?.action_type || 'LLM_CONFIRMATION',
  );
  const [actionPrompt, setActionPrompt] = useState(
    (config?.action_params as { prompt?: string })?.prompt || '',
  );
  const [replyWindowMinutes, setReplyWindowMinutes] = useState(config?.reply_window_minutes || 10);
  const [onTimeoutActionType, setOnTimeoutActionType] = useState<'LLM_CONFIRMATION' | ''>(
    config?.on_timeout_action_type || '',
  );
  const [onTimeoutActionPrompt, setOnTimeoutActionPrompt] = useState(
    (config?.on_timeout_action_params as { prompt?: string })?.prompt || '',
  );
  const [notificationEmails, setNotificationEmails] = useState<string[]>(
    config?.notification_emails || [],
  );
  const [emailInput, setEmailInput] = useState('');
  const [resultHandlers, setResultHandlers] = useState<ResultHandlerConfig[]>(
    config?.on_result_handlers || [],
  );
  const [expandedHandlers, setExpandedHandlers] = useState<Set<number>>(new Set());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      is_active: isActive,
      message_type: messageType,
      campaign_mode: campaignMode,
      action_type: actionType,
      action_params: { prompt: actionPrompt },
      on_timeout_action_type: onTimeoutActionType || null,
      on_timeout_action_params: onTimeoutActionType ? { prompt: onTimeoutActionPrompt } : null,
      reply_window_minutes: replyWindowMinutes,
      notification_emails: notificationEmails,
      on_result_handlers: resultHandlers,
      campaign_ids: selectedCampaignIds,
    });
  };

  const toggleCampaign = (id: string) => {
    setSelectedCampaignIds((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id],
    );
  };

  const addHandler = () => {
    setResultHandlers((prev) => [
      ...prev,
      { type: 'SEND_EMAIL', on_outcomes: ['CONFIRMED', 'NOT_CONFIRMED', 'TIMED_OUT'], params: {} },
    ]);
    setExpandedHandlers((prev) => new Set([...prev, resultHandlers.length]));
  };

  const removeHandler = (index: number) => {
    setResultHandlers((prev) => prev.filter((_, i) => i !== index));
    setExpandedHandlers((prev) => {
      const next = new Set<number>();
      for (const i of prev) {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      }
      return next;
    });
  };

  const updateHandler = (index: number, partial: Partial<ResultHandlerConfig>) => {
    setResultHandlers((prev) => prev.map((h, i) => (i === index ? { ...h, ...partial } : h)));
  };

  const toggleOutcome = (index: number, outcome: string) => {
    setResultHandlers((prev) =>
      prev.map((h, i) => {
        if (i !== index) return h;
        const outcomes = h.on_outcomes.includes(outcome)
          ? h.on_outcomes.filter((o) => o !== outcome)
          : [...h.on_outcomes, outcome];
        return { ...h, on_outcomes: outcomes };
      }),
    );
  };

  const toggleHandlerExpanded = (index: number) => {
    setExpandedHandlers((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <Dialog.Title className="text-lg font-semibold">
          {isEditing ? 'Edit SMS Action Config' : 'New SMS Action Config'}
        </Dialog.Title>
        <Dialog.Close asChild>
          <Button variant="ghost" size="icon">
            <X className="h-4 w-4" />
          </Button>
        </Dialog.Close>
      </div>
      <form onSubmit={handleSubmit} className="mt-4 max-h-[70vh] space-y-4 overflow-y-auto pe-2">
        {/* Name */}
        <div>
          <label className="text-sm font-medium">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="border-input bg-background mt-1 w-full rounded-md border px-3 py-2 text-sm"
            required
            minLength={2}
          />
        </div>

        {/* Active Toggle */}
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium">Active</label>
          <button
            type="button"
            role="switch"
            aria-checked={isActive}
            onClick={() => setIsActive(!isActive)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${isActive ? 'bg-primary' : 'bg-muted'}`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition-transform ${isActive ? 'translate-x-5' : 'translate-x-0'}`}
            />
          </button>
        </div>

        {/* Message Type */}
        <div>
          <label className="text-sm font-medium">Message Type</label>
          <select
            value={messageType}
            onChange={(e) => setMessageType(e.target.value as 'FOLLOWUP_SMS' | 'MISSED_CALL_SMS')}
            className="border-input bg-background mt-1 w-full rounded-md border px-3 py-2 text-sm"
          >
            <option value="FOLLOWUP_SMS">Follow-up SMS</option>
            <option value="MISSED_CALL_SMS">Missed Call SMS</option>
          </select>
        </div>

        {/* Campaign Mode */}
        <div>
          <label className="text-sm font-medium">Campaign Mode</label>
          <div className="mt-1 flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="campaignMode"
                value="INCLUDE"
                checked={campaignMode === 'INCLUDE'}
                onChange={() => setCampaignMode('INCLUDE')}
              />
              Include specific campaigns
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="campaignMode"
                value="EXCLUDE"
                checked={campaignMode === 'EXCLUDE'}
                onChange={() => setCampaignMode('EXCLUDE')}
              />
              Exclude specific campaigns
            </label>
          </div>
        </div>

        {/* Campaign Selection */}
        <div>
          <label className="text-sm font-medium">
            {campaignMode === 'INCLUDE' ? 'Include Campaigns' : 'Exclude Campaigns'}
          </label>
          <div className="border-input mt-1 max-h-40 overflow-y-auto rounded-md border p-2">
            {Array.isArray(campaigns) && campaigns.length > 0 ? (
              campaigns.map((campaign: { id: string; name: string }) => (
                <label
                  key={campaign.id}
                  className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedCampaignIds.includes(campaign.id)}
                    onChange={() => toggleCampaign(campaign.id)}
                  />
                  {campaign.name}
                </label>
              ))
            ) : (
              <p className="text-muted-foreground text-sm">No campaigns available</p>
            )}
          </div>
          {selectedCampaignIds.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {selectedCampaignIds.map((id) => {
                const c = Array.isArray(campaigns)
                  ? campaigns.find((c: { id: string }) => c.id === id)
                  : undefined;
                return (
                  <Badge key={id} variant="secondary" className="text-xs">
                    {(c as { name?: string })?.name || id}
                  </Badge>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Type */}
        <div>
          <label className="text-sm font-medium">Action Type</label>
          <select
            value={actionType}
            onChange={(e) => setActionType(e.target.value as 'LLM_CONFIRMATION')}
            className="border-input bg-background mt-1 w-full rounded-md border px-3 py-2 text-sm"
          >
            <option value="LLM_CONFIRMATION">LLM Confirmation</option>
          </select>
        </div>

        {/* Action Params - LLM Prompt */}
        <div>
          <label className="text-sm font-medium">Action Prompt</label>
          <p className="text-muted-foreground text-xs">
            Available placeholders: {'{{FOLLOW_UP_MESSAGE}}'}, {'{{MISSED_SMS_MESSAGE}}'},{' '}
            {'{{OUTBOUND_MESSAGE}}'}, {'{{REPLY_MESSAGE}}'}
          </p>
          <textarea
            value={actionPrompt}
            onChange={(e) => setActionPrompt(e.target.value)}
            className="border-input bg-background mt-1 w-full rounded-md border px-3 py-2 text-sm"
            rows={4}
            placeholder="Enter the LLM prompt template..."
          />
        </div>

        {/* Reply Window */}
        <div>
          <label className="text-sm font-medium">Reply Window (minutes)</label>
          <input
            type="number"
            value={replyWindowMinutes}
            onChange={(e) => setReplyWindowMinutes(parseInt(e.target.value, 10) || 10)}
            className="border-input bg-background mt-1 w-full rounded-md border px-3 py-2 text-sm"
            min={1}
            max={1440}
          />
        </div>

        {/* On Timeout Action */}
        <div>
          <label className="text-sm font-medium">On Timeout Action (optional)</label>
          <select
            value={onTimeoutActionType}
            onChange={(e) => setOnTimeoutActionType(e.target.value as 'LLM_CONFIRMATION' | '')}
            className="border-input bg-background mt-1 w-full rounded-md border px-3 py-2 text-sm"
          >
            <option value="">None</option>
            <option value="LLM_CONFIRMATION">LLM Confirmation</option>
          </select>
        </div>

        {/* On Timeout Action Params */}
        {onTimeoutActionType && (
          <div>
            <label className="text-sm font-medium">Timeout Action Prompt</label>
            <textarea
              value={onTimeoutActionPrompt}
              onChange={(e) => setOnTimeoutActionPrompt(e.target.value)}
              className="border-input bg-background mt-1 w-full rounded-md border px-3 py-2 text-sm"
              rows={3}
              placeholder="Enter the timeout action prompt template..."
            />
          </div>
        )}

        {/* Notification Emails */}
        <div>
          <label className="text-sm font-medium">Notification Emails</label>
          <p className="text-muted-foreground text-xs">
            Emails to notify when an SMS action completes
          </p>
          <div className="mt-1 flex gap-2">
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const email = emailInput.trim();
                  if (
                    email &&
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
                    !notificationEmails.includes(email)
                  ) {
                    setNotificationEmails([...notificationEmails, email]);
                    setEmailInput('');
                  }
                }
              }}
              className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
              placeholder="Enter email and press Enter"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const email = emailInput.trim();
                if (
                  email &&
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
                  !notificationEmails.includes(email)
                ) {
                  setNotificationEmails([...notificationEmails, email]);
                  setEmailInput('');
                }
              }}
            >
              Add
            </Button>
          </div>
          {notificationEmails.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {notificationEmails.map((email) => (
                <Badge key={email} variant="secondary" className="text-xs">
                  {email}
                  <button
                    type="button"
                    className="hover:text-destructive ml-1"
                    onClick={() =>
                      setNotificationEmails(notificationEmails.filter((e) => e !== email))
                    }
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Result Handlers */}
        <div>
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium">Result Handlers</label>
              <p className="text-muted-foreground text-xs">
                Actions to execute when an SMS action produces a result
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addHandler}>
              <Plus className="mr-1 h-3 w-3" />
              Add Handler
            </Button>
          </div>
          {resultHandlers.length > 0 && (
            <div className="mt-3 space-y-3">
              {resultHandlers.map((handler, index) => {
                const isExpanded = expandedHandlers.has(index);
                const typeLabel =
                  HANDLER_TYPES.find((t) => t.value === handler.type)?.label || handler.type;
                return (
                  <div key={index} className="rounded-md border p-3">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        className="flex items-center gap-2 text-sm font-medium"
                        onClick={() => toggleHandlerExpanded(index)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        Handler {index + 1}: {typeLabel}
                      </button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => removeHandler(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {isExpanded && (
                      <div className="mt-3 space-y-3 pl-6">
                        {/* Handler Type */}
                        <div>
                          <label className="text-xs font-medium">Type</label>
                          <select
                            value={handler.type}
                            onChange={(e) => {
                              const newType = e.target.value;
                              const params =
                                newType === 'UPDATE_CALL_OUTCOME'
                                  ? {
                                      outcome_map: { ...DEFAULT_OUTCOME_MAP },
                                      match_call_outcomes: [...callOutcomes],
                                    }
                                  : {};
                              updateHandler(index, { type: newType, params });
                            }}
                            className="border-input bg-background mt-1 w-full rounded-md border px-3 py-1.5 text-sm"
                          >
                            {HANDLER_TYPES.map((ht) => (
                              <option key={ht.value} value={ht.value}>
                                {ht.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Trigger Outcomes */}
                        <div>
                          <label className="text-xs font-medium">Trigger on outcomes</label>
                          <div className="mt-1 flex flex-wrap gap-3">
                            {OUTCOME_OPTIONS.map((outcome) => (
                              <label key={outcome} className="flex items-center gap-1.5 text-xs">
                                <input
                                  type="checkbox"
                                  checked={handler.on_outcomes.includes(outcome)}
                                  onChange={() => toggleOutcome(index, outcome)}
                                />
                                {outcome.replace('_', ' ')}
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Type-specific params */}
                        {handler.type === 'SEND_EMAIL' && (
                          <p className="text-muted-foreground text-xs italic">
                            Uses notification emails configured above
                          </p>
                        )}

                        {handler.type === 'UPDATE_CALL_OUTCOME' && (
                          <div className="space-y-3">
                            <div>
                              <label className="text-xs font-medium">Outcome Map</label>
                              <div className="mt-1 space-y-2">
                                {OUTCOME_OPTIONS.map((outcome) => (
                                  <div key={outcome} className="flex items-center gap-2">
                                    <span className="w-32 text-xs">
                                      {outcome.replace('_', ' ')} &rarr;
                                    </span>
                                    <input
                                      type="text"
                                      value={
                                        (handler.params?.outcome_map as Record<string, string>)?.[
                                          outcome
                                        ] || ''
                                      }
                                      onChange={(e) => {
                                        const currentMap =
                                          (handler.params?.outcome_map as Record<string, string>) ||
                                          {};
                                        updateHandler(index, {
                                          params: {
                                            ...handler.params,
                                            outcome_map: {
                                              ...currentMap,
                                              [outcome]: e.target.value,
                                            },
                                          },
                                        });
                                      }}
                                      className="border-input bg-background w-full rounded-md border px-2 py-1 text-xs"
                                      placeholder={DEFAULT_OUTCOME_MAP[outcome]}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium">Match Call Outcomes</label>
                              <div className="mt-1 flex flex-wrap gap-3">
                                {callOutcomes.map((co) => {
                                  const matchList =
                                    (handler.params?.match_call_outcomes as string[]) || [];
                                  return (
                                    <label key={co} className="flex items-center gap-1.5 text-xs">
                                      <input
                                        type="checkbox"
                                        checked={matchList.includes(co)}
                                        onChange={() => {
                                          const current =
                                            (handler.params?.match_call_outcomes as string[]) || [];
                                          const updated = current.includes(co)
                                            ? current.filter((c) => c !== co)
                                            : [...current, co];
                                          updateHandler(index, {
                                            params: {
                                              ...handler.params,
                                              match_call_outcomes: updated,
                                            },
                                          });
                                        }}
                                      />
                                      {co.replace('_', ' ')}
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}

                        {handler.type !== 'SEND_EMAIL' &&
                          handler.type !== 'UPDATE_CALL_OUTCOME' && (
                            <div>
                              <label className="text-xs font-medium">Params (JSON)</label>
                              <textarea
                                value={JSON.stringify(handler.params, null, 2)}
                                onChange={(e) => {
                                  try {
                                    const parsed = JSON.parse(e.target.value);
                                    updateHandler(index, { params: parsed });
                                  } catch {
                                    // Allow invalid JSON while typing
                                  }
                                }}
                                className="border-input bg-background mt-1 w-full rounded-md border px-2 py-1 font-mono text-xs"
                                rows={4}
                              />
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Dialog.Close asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : isEditing ? 'Save changes' : 'Create configuration'}
          </Button>
        </div>
      </form>
    </>
  );
};
